// THREE.js adapter over @physics/{sample,trace}. Wraps the pure pipelines so
// fieldline tracing returns THREE.Vector3 lists (scene-side wants Vector3 for
// BufferGeometry / TubeGeometry construction).

import { isOutsideBounds as isOutsidePure, trilinear } from '@physics/sample';
import { rk4Step as rk4StepPure, traceSync as traceSyncPure } from '@physics/trace';
import type { MutableVec3, PhysicsGrid } from '@physics/types';

interface ViewerGridShape {
  nx: number;
  ny: number;
  nz: number;
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  zMin: number;
  zMax: number;
  resolutionX: number;
  resolutionY: number;
  resolutionZ: number;
}

export interface FieldArrays {
  Bx: Float32Array | null | undefined;
  By: Float32Array | null | undefined;
  Bz: Float32Array | null | undefined;
}

interface Vec3Like { x: number; y: number; z: number; }

interface ThreeLike {
  Vector3: new (x: number, y: number, z: number) => Vec3Like;
}

export interface FieldlineCoreCtx {
  THREE: ThreeLike;
  grid: ViewerGridShape;
  getFieldArrays: () => FieldArrays;
}

export interface FieldlineCore {
  sampleVector(x: number, y: number, z: number): Vec3Like;
  rk4Step(pos: Vec3Like, h: number, dir: number): Vec3Like;
  isOutsideBounds(x: number, y: number, z: number): boolean;
  traceSync(start: Vec3Like, h: number, maxSteps: number): Vec3Like[];
}

export function createFieldlineCore(ctx: FieldlineCoreCtx): FieldlineCore {
  const { THREE, grid: vgrid, getFieldArrays } = ctx;

  // Remap viewer.grid (camelCase) into the lowercase-keyed grid that
  // physics/sample.ts + physics/trace.ts consume.
  const grid: PhysicsGrid = {
    nx: vgrid.nx, ny: vgrid.ny, nz: vgrid.nz,
    xmin: vgrid.xMin, xmax: vgrid.xMax,
    ymin: vgrid.yMin, ymax: vgrid.yMax,
    zmin: vgrid.zMin, zmax: vgrid.zMax,
    dx: vgrid.resolutionX, dy: vgrid.resolutionY, dz: vgrid.resolutionZ,
  };

  function sample(x: number, y: number, z: number): MutableVec3 {
    const { Bx, By, Bz } = getFieldArrays();
    if (!Bx || !By || !Bz) return [0, 0, 0];
    if (isOutsidePure(grid, x, y, z)) return [0, 0, 0];
    return [
      trilinear(Bx, grid, x, y, z),
      trilinear(By, grid, x, y, z),
      trilinear(Bz, grid, x, y, z),
    ];
  }

  function isOutside(x: number, y: number, z: number): boolean {
    return isOutsidePure(grid, x, y, z);
  }

  return {
    sampleVector(x, y, z) {
      const [a, b, c] = sample(x, y, z);
      return new THREE.Vector3(a, b, c);
    },
    rk4Step(pos, h, dir) {
      const [a, b, c] = rk4StepPure(sample, [pos.x, pos.y, pos.z], h, dir);
      return new THREE.Vector3(a, b, c);
    },
    isOutsideBounds: isOutside,
    traceSync(start, h, maxSteps) {
      const pts = traceSyncPure({ sample, isOutside }, [start.x, start.y, start.z], h, maxSteps);
      return pts.map(([x, y, z]) => new THREE.Vector3(x, y, z));
    },
  };
}
